/** The application provider tree. */
import * as React from "react";
import { useEffect, type ReactNode } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { PaletteMode, useMediaQuery } from "@mui/material";
import { SWRConfig } from "swr";

import ColorModeContext from "@/context/ColorModeContext";
import RouteGuard from "@/security/RouteGuard";
import TwoFactorProvider from "@/providers/TwoFactorProvider";
import { PaymentPollingProvider } from "@/context/PaymentPollingContext";
import { RecaptchaProvider } from "@/providers/RecaptchaProvider";
import { SnackbarErrorsProvider } from "@/providers/SnackbarErrorsProvider";
import { SnackbarProvider } from "@/providers/SnackbarProvider";
import { fetcher } from "@/utils/fetcher";
import { retryServerErrorsOnly } from "@/utils/swrRetry";
import { useAppTheme } from "@/theme";
import {
  hasDarkmodeCookie,
  removeDarkModeCookie,
  setDarkModeCookie,
} from "@/security/authentication";

/** Colour-mode state, lifted unchanged from `_app`. */
function useColorMode() {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const [mode, setMode] = React.useState<PaletteMode>("light");

  useEffect(() => {
    async function readDarkModeCookie() {
      const isDarkMode = await hasDarkmodeCookie();
      if (isDarkMode === true) {
        setMode("dark");
      } else if (isDarkMode === false) {
        setMode("light");
      } else {
        if (prefersDarkMode) {
          setMode("dark");
        } else {
          setMode("light");
        }
      }
    }
    readDarkModeCookie();
  }, [prefersDarkMode]);

  const toggleColorMode = React.useCallback(() => {
    setMode((prevMode: PaletteMode) => {
      if (prevMode === "light") {
        setDarkModeCookie();
      } else {
        removeDarkModeCookie();
      }
      return prevMode === "light" ? "dark" : "light";
    });
  }, []);

  return { mode, toggleColorMode };
}

export default function AppProviders({
  children,
  devTools,
  scripts,
}: {
  children: ReactNode;
  devTools?: ReactNode;
  scripts?: ReactNode;
}) {
  const { mode, toggleColorMode } = useColorMode();
  // Update the theme only if the mode changes
  const theme = useAppTheme(mode);

  return (
    <SWRConfig
      value={{
        fetcher,
        onErrorRetry: retryServerErrorsOnly,
        // SWR refetches every mounted key when the tab regains focus; once per
        // 30s per key is fresh enough and spares the backend an alt-tab storm.
        focusThrottleInterval: 30_000,
      }}
    >
      <ColorModeContext.Provider
        value={{
          mode,
          toggleColorMode,
        }}
      >
        <ThemeProvider theme={theme}>
          <CssBaseline enableColorScheme />
          <RecaptchaProvider>
            <TwoFactorProvider>
              <RouteGuard>
                <PaymentPollingProvider>
                  <SnackbarErrorsProvider>
                    <SnackbarProvider>
                      {scripts}
                      {children}
                      {devTools}
                    </SnackbarProvider>
                  </SnackbarErrorsProvider>
                </PaymentPollingProvider>
              </RouteGuard>
            </TwoFactorProvider>
          </RecaptchaProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </SWRConfig>
  );
}
